/**
 * 개인정보 처리방침 개정 고지 배너.
 *
 * 게이트가 아니다 — 앱을 막지 않고 알리기만 한다. 이번 개정으로 늘어난 처리는
 * 촬영 시점의 별도 동의로 이미 막혀 있어(§28조의8), 여기서 붙잡아 둘 이유가 없다.
 * 자세한 판정 근거는 `consentService.getPrivacyNoticeState` 주석 참고.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { logger } from '@/lib/logger'
import {
  PRIVACY_CHANGE_SUMMARY,
  PRIVACY_VERSION,
  acknowledgePrivacyNotice,
  formatVersionDate,
  getPrivacyNoticeState,
} from '@/services/consentService'

export function PrivacyUpdateNotice() {
  const { user } = useAuth()
  // 의존성은 객체가 아니라 uid다. `useAuth()`가 리렌더마다 새 객체를 돌려주면
  // 효과가 매번 다시 돌아 조회가 반복되고, 확인해서 내린 배너가 되살아난다.
  const uid = user?.uid
  const [needed, setNeeded] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!uid) return
    let cancelled = false
    getPrivacyNoticeState(uid).then((state) => {
      if (!cancelled) setNeeded(state.needed)
    })
    return () => {
      cancelled = true
    }
  }, [uid])

  const dismiss = useCallback(async () => {
    // 화면에서 먼저 내린다. 기록 실패로 배너가 남아 있으면 사용자는 같은 버튼을
    // 다시 누르는 것 말고 할 수 있는 일이 없다 — 다음 실행에 다시 뜨는 편이 낫다.
    // `dismissed`는 별도 상태다. 늦게 도착한 조회 응답이 이미 내린 배너를
    // 다시 올리지 못하게 하려면 "필요한가"와 "닫았는가"를 나눠 두어야 한다.
    setDismissed(true)
    if (!uid) return
    try {
      await acknowledgePrivacyNotice(uid)
    } catch (error) {
      logger.error('Failed to acknowledge privacy notice:', error)
    }
  }, [uid])

  if (!needed || dismissed) return null

  return (
    <div
      role="status"
      data-testid="privacy-update-notice"
      className="fixed inset-x-0 z-50 flex justify-center px-4"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)' }}
    >
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          개인정보 처리방침이 개정되었습니다
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          시행일 {formatVersionDate(PRIVACY_VERSION)}
        </p>
        <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
          {PRIVACY_CHANGE_SUMMARY.map((line) => (
            <li key={line} className="flex gap-1.5">
              <span aria-hidden="true">·</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Link
            to="/privacy-policy"
            className="text-xs font-medium text-blue-600 underline underline-offset-2 dark:text-blue-400"
            onClick={dismiss}
          >
            전문 보기
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="privacy-update-notice-ack"
            className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white dark:bg-gray-100 dark:text-gray-900"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  )
}
